import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { format } from "date-fns";
import { Icon } from "@trackcode/ui";
import DateRangeFilter from "features/dashboard/components/DateRangeFilter";
import {
  formatWencoWorkbookRevenue,
  getWencoUmsatzConfig,
  parseWencoUmsatzTeur,
  sumWencoUmsatzTeur
} from "common/wencoUmsatz";
import {
  isWencoDemoModeEnabled,
  WENCO_DEMO_MODE_CHANGED
} from "common/wencoDemoMode";
import WencoRhythmusConfigOverlay, {
  formatWencoRhythmusFromConfig,
  parseWencoRhythmusConfig
} from "./WencoRhythmusConfigOverlay";
import LayoutContext from "features/app/LayoutContext";

export function getLinkedWorkbookIdsForDriver(driverId, shipments = {}) {
  const normalizedDriverId = Number(driverId);
  if (!Number.isFinite(normalizedDriverId)) {
    return [];
  }
  const ids = new Set([`driver:${normalizedDriverId}`]);
  Object.values(shipments).forEach(shipment => {
    const memberId = Number(
      shipment?.driverId ||
        shipment?.responsible_member_id ||
        shipment?.last_responsible_member_id
    );
    if (!Number.isFinite(memberId) || memberId !== normalizedDriverId) {
      return;
    }
    const tourId =
      shipment?.tourId ||
      shipment?.responsible_tour_id ||
      shipment?.last_responsible_tour_id;
    if (tourId) {
      ids.add(`tour:${tourId}`);
    }
  });
  return Array.from(ids);
}

const TAB_ORDER = [
  "vordisponiert",
  "ausstehend",
  "disponiert",
  "erledigt",
  "wiederkehrend"
];

const TAB_ORDER_WENCO_DEMO = [
  "wiederkehrend",
  "vordisponiert",
  "ausstehend",
  "disponiert",
  "erledigt"
];

const WORKBOOK_VIEW_MINI = "mini";
const WORKBOOK_VIEW_COLLAPSED = "collapsed";
const WORKBOOK_VIEW_EXPANDED = "expanded";

const getWorkbookViewState = workbook => {
  if (workbook?.viewState) {
    if (
      workbook.viewState === WORKBOOK_VIEW_COLLAPSED &&
      !workbook.isInbox
    ) {
      return WORKBOOK_VIEW_MINI;
    }
    return workbook.viewState;
  }
  if (workbook?.expanded) return WORKBOOK_VIEW_EXPANDED;
  return workbook?.isInbox ? WORKBOOK_VIEW_COLLAPSED : WORKBOOK_VIEW_MINI;
};

const isWorkbookExpanded = workbook =>
  getWorkbookViewState(workbook) === WORKBOOK_VIEW_EXPANDED;

const isWorkbookMini = workbook =>
  getWorkbookViewState(workbook) === WORKBOOK_VIEW_MINI;

const getTabDisplayLabel = tab =>
  tab === "wiederkehrend" ? "Serienaufgaben" : tab;

const getWorkbookDriverId = workbook => {
  if (!workbook?.id?.startsWith("driver:")) return null;
  return workbook.id.replace("driver:", "");
};

const getDriverFirstname = (driverId, drivers) => {
  if (!driverId) return "";
  const driver =
    drivers?.[driverId] ??
    Object.values(drivers || {}).find(
      entry => String(entry.id) === String(driverId)
    );
  return String(driver?.firstname || "").trim();
};

const getWorkbookMiniLabel = workbook => {
  const driverId = getWorkbookDriverId(workbook);
  if (driverId) return driverId;
  const name = String(workbook.name || "").trim();
  if (/^\d+$/.test(name)) return name;
  return workbook.number || name || "—";
};

const getDefaultActiveTab = (_workbook, wencoDemoMode) => {
  if (wencoDemoMode) return "wiederkehrend";
  return "disponiert";
};

const INBOX_ID = "__inbox__";

const normalize = value =>
  String(value || "")
    .trim()
    .toUpperCase();

const includesAny = (text, keys) => keys.some(key => text.includes(key));

const getDriverName = driver => {
  if (!driver) return "";
  if (
    driver.firstname &&
    driver.lastname &&
    driver.firstname === driver.lastname
  ) {
    return driver.firstname;
  }
  return [driver.firstname, driver.lastname].filter(Boolean).join(" ");
};

const buildAddress = shipment => {
  const address = shipment?.recipient?.address || {};
  return {
    street: address.street || "-",
    zip: address.zip ? String(address.zip) : "",
    city: address.city || "",
    country: address.country || ""
  };
};

const getTimeWindow = shipment => {
  const visitTimeWindows = shipment?.wenco?.visitTimeWindows;
  if (Array.isArray(visitTimeWindows) && visitTimeWindows.length > 0) {
    const compact = visitTimeWindows
      .map(window => {
        const from = String(window?.from || "").trim();
        const till = String(window?.till || "").trim();
        if (!from || !till) return null;
        return `${from} - ${till}`;
      })
      .filter(Boolean);
    if (compact.length > 0) {
      return compact.join(" / ");
    }
  }

  const from = shipment?.recipient?.time?.from || null;
  const till = shipment?.recipient?.time?.till || null;
  if (from && till)
    return `${format(from, "HH:mm")} - ${format(till, "HH:mm")}`;
  if (from) return `ab ${format(from, "HH:mm")}`;
  if (till) return `bis ${format(till, "HH:mm")}`;
  return "-";
};

/** Due date + time window for table; omit comma when there is no real time window. */
const formatTerminierungCell = (faelligkeit, zeitfenster) => {
  const due = faelligkeit && String(faelligkeit).trim() ? faelligkeit : "—";
  const tw = String(zeitfenster ?? "")
    .trim()
    .replace(/\u00a0/g, " ");
  if (!tw || tw === "-") return due;
  return `${due}, ${tw}`;
};

const getShipmentStatusId = shipment => {
  const sid = shipment?.status_id;
  if (sid == null || sid === "") return NaN;
  return Number(sid);
};

const getTaskStatusId = task => {
  const sid = task?.status_id;
  if (sid == null || sid === "") return NaN;
  return Number(sid);
};

const isHindernisTask = task => getTaskStatusId(task) === 7;

const isDoneByStatus = shipment =>
  getShipmentStatusId(shipment) === 6 ||
  getShipmentStatusId(shipment) === 7 ||
  includesAny(normalize(shipment.statusLabel), [
    "ZUGESTELLT",
    "ERLEDIGT",
    "ABGEHOLT",
    "BELADEN",
    "VERLADEN",
    "GELADEN",
    "ABGESCHLOSSEN",
    "AUSGELIEFERT",
    "UNTERSCHRIEBEN",
    "SIGNIERT",
    "HINDERNIS"
  ]);

const isTaskDone = task =>
  isHindernisTask(task) ||
  Boolean(task.done) ||
  task.aufgabeStatus === "erledigt";

const getVisibleRowsForTab = (workbook, tab) =>
  tab === "erledigt"
    ? workbook.tasks.filter(isTaskDone)
    : workbook.tasks.filter(
        task => task.status === tab && !isTaskDone(task)
      );

const parseRecurrence = shipment => {
  const pattern = shipment?.recurrence?.pattern;
  if (!pattern) return "Wöchentlich";
  try {
    const parsed = typeof pattern === "string" ? JSON.parse(pattern) : pattern;
    const dayOfWeek = String(parsed.dayofweek || "").trim();
    if (dayOfWeek === "*") return "Täglich";
    if (!dayOfWeek) return "Wöchentlich";
    const map = {
      "0": "So",
      "1": "Mo",
      "2": "Di",
      "3": "Mi",
      "4": "Do",
      "5": "Fr",
      "6": "Sa"
    };
    const days = dayOfWeek
      .split(",")
      .map(day => map[String(day).trim()])
      .filter(Boolean);
    return days.length ? `Wöchentlich, ${days.join(", ")}` : "Wöchentlich";
  } catch (error) {
    return "Wöchentlich";
  }
};

const formatWencoRhythmus = shipment => {
  const weeksRaw = String(shipment?.wenco?.rhythmus ?? "").trim();
  const dayLabel = String(shipment?.wenco?.besuchstagLabel ?? "").trim();
  if (!weeksRaw) return dayLabel || "";
  const weeks = Number(weeksRaw);
  const weeksPart =
    Number.isFinite(weeks) && weeks > 0
      ? `Alle ${weeks} Wochen`
      : `Alle ${weeksRaw} Wochen`;
  return dayLabel ? `${weeksPart}, ${dayLabel}` : weeksPart;
};

const getNetworkLabel = shipment => {
  const raw =
    shipment?.import_logisticcompany_external_label ??
    shipment?.importLogisticcompanyExternalLabel;
  if (raw == null || raw === "") return "";
  return String(raw).trim();
};

const getPlannedRevenueTeur = shipment =>
  parseWencoUmsatzTeur(shipment?.wenco?.umsatz);

const getWencoTaskLabel = shipment => {
  const duration = Number(shipment?.wenco?.besuchsdauer);
  if (Number.isFinite(duration)) {
    if (duration >= 160) return "Regalservice durchführen";
    if (duration >= 120) return "Platzierungen optimieren";
    if (duration >= 80) return "Sortimente pflegen";
    return "Nachbestücken";
  }
  return "Nachbestücken";
};

const deriveWorkflowStatus = (shipment, wencoDemoMode = false) => {
  if (wencoDemoMode && shipment?.wenco) return "wiederkehrend";
  if (shipment.isRecurring) return "wiederkehrend";
  const label = normalize(shipment.statusLabel);
  if (includesAny(label, ["VORDISPON"])) return "vordisponiert";
  if (includesAny(label, ["ANGEFRAG", "AUSSTEHEND", "VORGESCHLAGEN"]))
    return "ausstehend";
  if (shipment.driverId || shipment.tourId) return "disponiert";
  return "ausstehend";
};

const toTask = (shipment, wencoDemoMode = false) => {
  const address = buildAddress(shipment);
  const doneDate = shipment.update_datetime
    ? new Date(shipment.update_datetime)
    : null;
  const isWencoTask = Boolean(shipment?.wenco);
  const taskType = isWencoTask
    ? getWencoTaskLabel(shipment)
    : shipment.isPickedUp
    ? "zustellen"
    : "abholen";
  const kundennummer =
    shipment.ext_reference || shipment.group_reference || "";
  return {
    id: shipment.uid,
    sourceId: shipment.uid,
    status_id: shipment.status_id,
    status: deriveWorkflowStatus(shipment, wencoDemoMode),
    done: isDoneByStatus(shipment),
    doneDate,
    aufgabeStatus: null,
    ort: `${address.street}, ${[address.zip, address.city]
      .filter(Boolean)
      .join(" ")}, ${address.country}`
      .replace(", ,", ",")
      .trim(),
    addrStreet: String(address.street || "").trim(),
    addrZip: String(address.zip || "").trim(),
    addrCity: String(address.city || "").trim(),
    addrCountry: String(address.country || "").trim(),
    name:
      shipment?.recipient?.company ||
      shipment?.recipient?.name ||
      shipment?.sender?.company ||
      shipment?.sender?.name ||
      "",
    kundennummer,
    isWencoTask,
    typ: taskType,
    anzahl: shipment.shipmentIds ? shipment.shipmentIds.length : 1,
    netzwerk: getNetworkLabel(shipment),
    plannedRevenueTeur: getPlannedRevenueTeur(shipment),
    auftragsnr:
      shipment.ext_reference ||
      shipment.group_reference ||
      shipment.trackcode ||
      "",
    trackingCode: shipment.group_reference || shipment.trackcode || "",
    faelligkeit: shipment.dueDate
      ? format(shipment.dueDate, "DD.MM.YYYY")
      : "-",
    dueSortValue: shipment.dueDate
      ? new Date(shipment.dueDate).getTime()
      : null,
    zeitfenster: getTimeWindow(shipment),
    rhythmus:
      wencoDemoMode && isWencoTask
        ? formatWencoRhythmus(shipment)
        : shipment.isRecurring
        ? parseRecurrence(shipment)
        : "",
    templateId: shipment.isRecurring ? shipment.id : null
  };
};

const groupByShipmentReference = shipments => {
  const grouped = {};
  Object.values(shipments || {}).forEach(shipment => {
    const key = shipment.group_reference || shipment.uid;
    if (!grouped[key]) {
      grouped[key] = {
        ...shipment,
        shipmentIds: [shipment.uid]
      };
      return;
    }
    grouped[key].shipmentIds.push(shipment.uid);
    if (!getNetworkLabel(grouped[key]) && getNetworkLabel(shipment)) {
      grouped[key] = {
        ...grouped[key],
        import_logisticcompany_external_label:
          shipment.import_logisticcompany_external_label
      };
    }
  });
  return Object.values(grouped);
};

const buildWorkbooksFromShipments = (
  shipments,
  driverList = [],
  wencoDemoMode = false
) => {
  const all = groupByShipmentReference(shipments);
  const grouped = {
    [INBOX_ID]: {
      id: INBOX_ID,
      number: "0",
      name: "Nicht zugeordnet",
      isInbox: true,
      viewState: WORKBOOK_VIEW_COLLAPSED,
      fahrzeug: "",
      mitarbeiter: "",
      clearing: "",
      tasks: []
    }
  };

  const defaultViewState = wencoDemoMode
    ? WORKBOOK_VIEW_MINI
    : WORKBOOK_VIEW_COLLAPSED;

  all.forEach(shipment => {
    const tourId =
      shipment.tourId ||
      shipment.responsible_tour_id ||
      shipment.last_responsible_tour_id;
    const driverId =
      shipment.driverId ||
      shipment.responsible_member_id ||
      shipment.last_responsible_member_id;
    const driverName = shipment.driver ? getDriverName(shipment.driver) : "";
    const workbookId = tourId
      ? `tour:${tourId}`
      : driverId
      ? `driver:${driverId}`
      : INBOX_ID;

    if (!grouped[workbookId]) {
      grouped[workbookId] = {
        id: workbookId,
        number: String(Object.keys(grouped).length),
        name:
          (shipment.tour && shipment.tour.label) ||
          driverName ||
          `Arbeitsmappe ${Object.keys(grouped).length}`,
        isInbox: false,
        viewState: defaultViewState,
        fahrzeug: "",
        mitarbeiter: driverName,
        clearing: "",
        tasks: []
      };
    }

    grouped[workbookId].tasks.push(toTask(shipment, wencoDemoMode));
  });

  driverList.forEach(driver => {
    if (!driver || driver.id == null) return;
    const workbookId = `driver:${driver.id}`;
    if (grouped[workbookId]) return;
    const driverName = getDriverName(driver);
    grouped[workbookId] = {
      id: workbookId,
      number: String(Object.keys(grouped).length),
      name: driverName || String(driver.id),
      isInbox: false,
      viewState: defaultViewState,
      fahrzeug: "",
      mitarbeiter: driverName,
      clearing: "",
      tasks: []
    };
  });

  return Object.values(grouped).sort((a, b) => {
    if (a.isInbox) return -1;
    if (b.isInbox) return 1;
    return a.name.localeCompare(b.name, "de");
  });
};

const buildShipmentSyncKey = (shipments = {}) =>
  Object.keys(shipments)
    .sort()
    .map(id => {
      const shipment = shipments[id];
      return `${id}:${shipment?.update_datetime ?? ""}:${shipment?.status_id ?? ""}`;
    })
    .join("|");

const buildDriverSyncKey = (drivers = {}) =>
  Object.keys(drivers)
    .sort()
    .join(",");

const workbooksStructureKey = workbooks =>
  workbooks
    .map(
      workbook =>
        `${workbook.id}:${workbook.tasks.length}:${getWorkbookViewState(workbook)}`
    )
    .join("|");

const formatOrderNumber = value => {
  if (!value) return "—";
  if (value.length <= 10) return value;
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
};

const toPerfekt = type => {
  if (type === "abholen") return "abgeholt";
  if (type === "einräumen") return "eingeräumt";
  if (
    type === "Sortimente pflegen" ||
    type === "Platzierungen optimieren" ||
    type === "Regalservice durchführen" ||
    type === "Nachbestücken"
  ) {
    return "erledigt";
  }
  return "zugestellt";
};

/** Aufgaben-Bezeichnung: feste Status-IDs aus Disposition (siehe TrackCode-Statusmodell). */
const getAufgabeVerb = (task, isDone) => {
  const sid = task.status_id != null ? Number(task.status_id) : NaN;
  if (sid === 7) return "Hindernis";
  if (sid === 4) return task.isWencoTask ? "erledigt" : "abgeholt";
  if (isDone) return toPerfekt(task.typ);
  return task.typ;
};

const SORT_COL_ADDRESS = "address";
const SORT_COL_NAME = "name";
const SORT_COL_AUFGABE = "aufgabe";
const SORT_COL_NETZ = "netzwerk";
const SORT_COL_AUFTRAG = "auftrag";
const SORT_COL_TERMIN = "terminierung";
const SORT_COL_RHYTHMUS = "rhythmus";

const PAGE_SIZE_STORAGE_KEY = "workbook_disposition_page_size";
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

/** Accent for completed tasks (checkmark + left stripe). */
const WORKBOOK_DONE_ACCENT = "#7eb73d";

/** Accent for Hindernis (status_id 7). */
const WORKBOOK_HINDERNIS_ACCENT = "#e6a020";

/** Uniform tbody row height (matches rendered data rows, ~39px incl. padding). */
const WORKBOOK_TBODY_ROW_HEIGHT_PX = 39;
const WORKBOOK_TABLE_COLSPAN = 8;

const ADDRESS_LINE_SEGMENTS = [
  { field: "street", text: "Straße", prefix: "" },
  { field: "zip", text: "PLZ", prefix: ", " },
  { field: "city", text: "Stadt", prefix: ", " },
  { field: "country", text: "Land", prefix: ", " }
];

/** 1-based page numbers plus "ellipsis" gaps (currentPage0 is zero-based). */
const buildPaginationPageList = (currentPage0, totalPages) => {
  if (totalPages <= 1) return [1];
  const cur = currentPage0 + 1;
  const delta = 2;
  const range = [];
  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || (i >= cur - delta && i <= cur + delta)) {
      range.push(i);
    }
  }
  const out = [];
  let l;
  for (const i of range) {
    if (l != null) {
      if (i - l === 2) out.push(l + 1);
      else if (i - l > 1) out.push("ellipsis");
    }
    out.push(i);
    l = i;
  }
  return out;
};

const addressFieldValue = (task, field) => {
  switch (field) {
    case "zip":
      return task.addrZip || "";
    case "city":
      return task.addrCity || "";
    case "country":
      return task.addrCountry || "";
    case "full":
      return task.ort || "";
    case "street":
    default:
      return task.addrStreet || "";
  }
};

const zeitfensterSortKey = z => {
  const s = String(z ?? "").trim();
  if (!s || s === "-") return "";
  return s;
};

const sortVisibleRows = (tasks, sortConfig) => {
  if (!sortConfig || !sortConfig.key) return tasks.slice();
  const dirMult = sortConfig.dir === "desc" ? -1 : 1;
  const rows = tasks.slice();
  const addrField = sortConfig.addrField || "street";

  rows.sort((a, b) => {
    let cmp = 0;
    switch (sortConfig.key) {
      case SORT_COL_ADDRESS: {
        const fa = addressFieldValue(a, addrField);
        const fb = addressFieldValue(b, addrField);
        cmp = fa.localeCompare(fb, "de", {
          numeric: true,
          sensitivity: "base"
        });
        break;
      }
      case SORT_COL_NAME:
        cmp = (a.name || "").localeCompare(b.name || "", "de", {
          sensitivity: "base"
        });
        break;
      case SORT_COL_AUFGABE: {
        const na = Number(a.anzahl) || 0;
        const nb = Number(b.anzahl) || 0;
        if (na !== nb) cmp = na < nb ? -1 : 1;
        else {
          const va = getAufgabeVerb(a, Boolean(a.done));
          const vb = getAufgabeVerb(b, Boolean(b.done));
          cmp = va.localeCompare(vb, "de", { sensitivity: "base" });
        }
        break;
      }
      case SORT_COL_NETZ:
        cmp = (a.netzwerk || "").localeCompare(b.netzwerk || "", "de", {
          sensitivity: "base"
        });
        break;
      case SORT_COL_AUFTRAG:
        cmp = String(a.auftragsnr || "").localeCompare(
          String(b.auftragsnr || ""),
          "de",
          {
            numeric: true,
            sensitivity: "base"
          }
        );
        break;
      case SORT_COL_TERMIN: {
        const ta = a.dueSortValue != null ? a.dueSortValue : -Infinity;
        const tb = b.dueSortValue != null ? b.dueSortValue : -Infinity;
        if (ta !== tb) cmp = ta < tb ? -1 : 1;
        else {
          cmp = zeitfensterSortKey(a.zeitfenster).localeCompare(
            zeitfensterSortKey(b.zeitfenster),
            "de"
          );
        }
        break;
      }
      case SORT_COL_RHYTHMUS:
        cmp = (a.rhythmus || "").localeCompare(b.rhythmus || "", "de", {
          sensitivity: "base"
        });
        break;
      default:
        cmp = 0;
    }
    if (cmp !== 0) return dirMult * cmp;
    return String(a.id).localeCompare(String(b.id));
  });
  return rows;
};

const PackageIcon = ({ muted }) => (
  <Icon
    name="parcel"
    size="sm"
    fill="currentColor"
    style={{
      display: "block",
      flexShrink: 0,
      opacity: muted ? 0.28 : 0.5
    }}
  />
);

const SortArrows = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  gap: 0;
  line-height: 0;
  flex-shrink: 0;
  align-self: center;
`;

const SortArrowPair = ({ activeDir }) => {
  const sorted = activeDir != null;
  const emphasizeAsc = sorted && activeDir === "asc";
  const emphasizeDesc = sorted && activeDir === "desc";
  return (
    <SortArrows aria-hidden>
      <svg
        viewBox="0 0 10 6"
        width="10"
        height="8"
        fill="none"
        style={{ opacity: emphasizeAsc ? 1 : 0.35 }}
      >
        <path
          d="M2.5 4.5L5 2L7.5 4.5"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <svg
        viewBox="0 0 10 6"
        width="10"
        height="8"
        fill="none"
        style={{ opacity: emphasizeDesc ? 1 : 0.35 }}
      >
        <path
          d="M2.5 1.5L5 4L7.5 1.5"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </SortArrows>
  );
};

const sortArrowFor = (sortCfg, columnKey) => {
  if (sortCfg?.key !== columnKey) return null;
  return <SortArrowPair activeDir={sortCfg.dir} />;
};

SortArrowPair.propTypes = {
  activeDir: PropTypes.oneOf(["asc", "desc", null])
};

SortArrowPair.defaultProps = {
  activeDir: null
};

const RowMoveMenu = ({ options, onMove }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onOutsideClick = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, []);

  return (
    <MoveMenuWrap ref={ref}>
      <MoveButton
        type="button"
        onClick={event => {
          event.stopPropagation();
          setOpen(prev => !prev);
        }}
      >
        →
      </MoveButton>
      {open && (
        <MoveMenuPanel>
          {options.map(entry => (
            <MoveMenuItem
              key={entry.id}
              type="button"
              onClick={() => {
                onMove(entry.id);
                setOpen(false);
              }}
            >
              #{entry.number} {entry.name}
            </MoveMenuItem>
          ))}
        </MoveMenuPanel>
      )}
    </MoveMenuWrap>
  );
};

/** Content for global ActionBar (full width); same pipeline as list filters. */
export const WorkbookSchedulingTopbar = ({
  filterDateRange,
  onFilterDateRangeChange,
  shipmentCount,
  shipmentCountForDate,
  onViewModeToggle,
  searchText,
  onSearchTextChange
}) => (
  <WorkbookSchedulingTopbarRoot>
    <TopbarDateWrap>
      {filterDateRange?.start &&
      filterDateRange?.end &&
      onFilterDateRangeChange ? (
        <DateRangeFilter
          startDate={filterDateRange.start}
          endDate={filterDateRange.end}
          onDateChange={onFilterDateRangeChange}
          panelAlign="start"
        />
      ) : null}
    </TopbarDateWrap>
    <TopbarTools>
      <Search
        placeholder="Arbeitsmappe suchen..."
        value={searchText}
        onChange={event => onSearchTextChange(event.target.value)}
      />
    </TopbarTools>
    <TopbarRightCluster>
      {typeof shipmentCount === "number" &&
      typeof shipmentCountForDate === "number" ? (
        <TopbarCount>
          {shipmentCount} von {shipmentCountForDate}
        </TopbarCount>
      ) : null}
      {typeof onViewModeToggle === "function" ? (
        <TopbarViewToggle type="button" onClick={onViewModeToggle}>
          Zur Listenansicht
        </TopbarViewToggle>
      ) : null}
    </TopbarRightCluster>
  </WorkbookSchedulingTopbarRoot>
);

WorkbookSchedulingTopbar.propTypes = {
  filterDateRange: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  }),
  onFilterDateRangeChange: PropTypes.func,
  shipmentCount: PropTypes.number,
  shipmentCountForDate: PropTypes.number,
  onViewModeToggle: PropTypes.func,
  searchText: PropTypes.string.isRequired,
  onSearchTextChange: PropTypes.func.isRequired
};

WorkbookSchedulingTopbar.defaultProps = {
  filterDateRange: null,
  onFilterDateRangeChange: null,
  shipmentCount: undefined,
  shipmentCountForDate: undefined,
  onViewModeToggle: null
};

const WorkbookView = ({
  shipments,
  drivers,
  searchText,
  onSearchTextChange
}) => {
  const {
    registerMapDriverClickHandler,
    registerWorkbookDriverFocusHandler,
    setActiveMapMemberIds
  } = useContext(LayoutContext) || {};
  const [wencoDemoMode, setWencoDemoMode] = useState(isWencoDemoModeEnabled());

  useEffect(() => {
    const onDemoChange = () => setWencoDemoMode(isWencoDemoModeEnabled());
    window.addEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
    return () =>
      window.removeEventListener(WENCO_DEMO_MODE_CHANGED, onDemoChange);
  }, []);

  const driverList = useMemo(
    () => Object.values(drivers || {}),
    [drivers]
  );
  const shipmentSyncKey = useMemo(
    () => buildShipmentSyncKey(shipments),
    [shipments]
  );
  const driverSyncKey = useMemo(
    () => buildDriverSyncKey(drivers),
    [drivers]
  );
  const [pageSize, setPageSize] = useState(() => {
    try {
      const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      const v = Number(stored);
      if (PAGE_SIZE_OPTIONS.includes(v)) return v;
    } catch (e) {
      /* ignore */
    }
    return 10;
  });
  const [sortByWorkbook, setSortByWorkbook] = useState({});
  const [pageByWorkbook, setPageByWorkbook] = useState({});

  const [workbooks, setWorkbooks] = useState(() =>
    buildWorkbooksFromShipments(shipments, driverList, wencoDemoMode)
  );
  const [activeTabByWorkbook, setActiveTabByWorkbook] = useState({});
  const [selectedByWorkbook, setSelectedByWorkbook] = useState({});
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [rhythmusEditor, setRhythmusEditor] = useState(null);
  const menuRef = useRef(null);

  const scrollToWorkbook = useCallback(workbookId => {
    if (!workbookId) {
      return;
    }
    window.requestAnimationFrame(() => {
      const element = document.querySelector(
        `[data-workbook-id="${workbookId}"]`
      );
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest"
        });
      }
    });
  }, []);

  const applyDriverWorkbookViewState = useCallback(
    (driverId, viewState) => {
      const normalizedDriverId = Number(driverId);
      if (!Number.isFinite(normalizedDriverId)) {
        return;
      }
      const linkedIds = new Set(
        getLinkedWorkbookIdsForDriver(normalizedDriverId, shipments)
      );
      if (!linkedIds.size) {
        return;
      }

      setWorkbooks(prev => {
        const scrollTargetId =
          prev.find(workbook => workbook.id === `driver:${normalizedDriverId}`)
            ?.id ||
          prev.find(workbook => linkedIds.has(workbook.id))?.id ||
          null;

        const next = prev.map(workbook => {
          if (!linkedIds.has(workbook.id) || workbook.isInbox) {
            return workbook;
          }
          return {
            ...workbook,
            viewState
          };
        });

        if (viewState === WORKBOOK_VIEW_EXPANDED) {
          scrollToWorkbook(scrollTargetId);
        }

        return next;
      });
    },
    [shipments, scrollToWorkbook]
  );

  const toggleWorkbooksForMapDriver = useCallback(
    driverId => {
      const normalizedDriverId = Number(driverId);
      if (!Number.isFinite(normalizedDriverId)) {
        return;
      }
      const driverWorkbookId = `driver:${normalizedDriverId}`;
      const linkedIds = new Set(
        getLinkedWorkbookIdsForDriver(normalizedDriverId, shipments)
      );
      if (!linkedIds.size) {
        return;
      }

      setWorkbooks(prev => {
        const driverWorkbook = prev.find(
          workbook => workbook.id === driverWorkbookId
        );
        const shouldCollapse =
          driverWorkbook && isWorkbookExpanded(driverWorkbook);
        const viewState = shouldCollapse
          ? WORKBOOK_VIEW_MINI
          : WORKBOOK_VIEW_EXPANDED;

        const scrollTargetId =
          prev.find(workbook => linkedIds.has(workbook.id))?.id || null;

        const next = prev.map(workbook => {
          if (!linkedIds.has(workbook.id) || workbook.isInbox) {
            return workbook;
          }
          return {
            ...workbook,
            viewState
          };
        });

        if (!shouldCollapse) {
          scrollToWorkbook(scrollTargetId);
        }

        return next;
      });
    },
    [shipments, scrollToWorkbook]
  );

  const expandWorkbooksForDriver = useCallback(
    driverId => {
      applyDriverWorkbookViewState(driverId, WORKBOOK_VIEW_EXPANDED);
    },
    [applyDriverWorkbookViewState]
  );

  useEffect(() => {
    if (typeof registerMapDriverClickHandler !== "function") {
      return undefined;
    }
    registerMapDriverClickHandler(toggleWorkbooksForMapDriver);
    return () => registerMapDriverClickHandler(null);
  }, [registerMapDriverClickHandler, toggleWorkbooksForMapDriver]);

  useEffect(() => {
    if (typeof registerWorkbookDriverFocusHandler !== "function") {
      return undefined;
    }
    registerWorkbookDriverFocusHandler(expandWorkbooksForDriver);
    return () => registerWorkbookDriverFocusHandler(null);
  }, [registerWorkbookDriverFocusHandler, expandWorkbooksForDriver]);

  useEffect(() => {
    if (!wencoDemoMode || typeof setActiveMapMemberIds !== "function") {
      return undefined;
    }
    const memberIds = [
      ...new Set(
        workbooks
          .filter(
            workbook =>
              isWorkbookExpanded(workbook) && getWorkbookDriverId(workbook)
          )
          .map(workbook => Number(getWorkbookDriverId(workbook)))
          .filter(Number.isFinite)
      )
    ];
    setActiveMapMemberIds(memberIds);
    return undefined;
  }, [workbooks, wencoDemoMode, setActiveMapMemberIds]);

  useEffect(() => {
    if (wencoDemoMode || typeof setActiveMapMemberIds !== "function") {
      return undefined;
    }
    setActiveMapMemberIds([]);
    return undefined;
  }, [wencoDemoMode, setActiveMapMemberIds]);

  useEffect(
    () => () => {
      if (typeof setActiveMapMemberIds === "function") {
        setActiveMapMemberIds([]);
      }
    },
    [setActiveMapMemberIds]
  );

  const openRhythmusEditor = (workbookId, task) => {
    setRhythmusEditor({
      workbookId,
      taskId: task.id,
      initialConfig: parseWencoRhythmusConfig(task.rhythmus)
    });
  };

  const closeRhythmusEditor = () => setRhythmusEditor(null);

  const applyRhythmusEditor = config => {
    if (!rhythmusEditor) return;
    const rhythmus = formatWencoRhythmusFromConfig(config);
    setWorkbooks(prev =>
      prev.map(workbook =>
        workbook.id !== rhythmusEditor.workbookId
          ? workbook
          : {
              ...workbook,
              tasks: workbook.tasks.map(task =>
                task.id === rhythmusEditor.taskId ? { ...task, rhythmus } : task
              )
            }
      )
    );
    setRhythmusEditor(null);
  };

  useEffect(() => {
    setWorkbooks(prev => {
      const built = buildWorkbooksFromShipments(
        shipments,
        driverList,
        wencoDemoMode
      );
      const prevById = Object.fromEntries(prev.map(w => [w.id, w]));
      const next = built.map(workbook => ({
        ...workbook,
        viewState:
          prevById[workbook.id]?.viewState ??
          getWorkbookViewState(prevById[workbook.id]) ??
          workbook.viewState
      }));
      if (workbooksStructureKey(prev) === workbooksStructureKey(next)) {
        return prev;
      }
      return next;
    });
  }, [shipmentSyncKey, driverSyncKey, shipments, driverList, wencoDemoMode]);

  useEffect(() => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch (e) {
      /* ignore */
    }
  }, [pageSize]);

  useEffect(() => {
    const handleClick = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = useMemo(
    () =>
      workbooks.filter(
        workbook =>
          workbook.isInbox ||
          workbook.name.toLowerCase().includes(searchText.toLowerCase()) ||
          workbook.number.includes(searchText)
      ),
    [workbooks, searchText]
  );

  useEffect(() => {
    setPageByWorkbook(prev => {
      const next = { ...prev };
      let changed = false;
      filtered.forEach(workbook => {
        const activeTab =
          activeTabByWorkbook[workbook.id] ||
          getDefaultActiveTab(workbook, wencoDemoMode);
        const visible = getVisibleRowsForTab(workbook, activeTab);
        const sorted = sortVisibleRows(visible, sortByWorkbook[workbook.id]);
        const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
        const p = prev[workbook.id] ?? 0;
        const clamped = Math.min(Math.max(0, p), totalPages - 1);
        if (p !== clamped) {
          next[workbook.id] = clamped;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [filtered, activeTabByWorkbook, sortByWorkbook, pageSize, wencoDemoMode]);

  const setWorkbook = updater => {
    setWorkbooks(prev =>
      typeof updater === "function" ? updater(prev) : updater
    );
  };

  const getActiveTab = (workbookId, workbook) =>
    activeTabByWorkbook[workbookId] ||
    getDefaultActiveTab(workbook, wencoDemoMode);
  const setActiveTab = (workbookId, nextTab) => {
    setActiveTabByWorkbook(prev => ({ ...prev, [workbookId]: nextTab }));
    setSelectedByWorkbook(prev => ({ ...prev, [workbookId]: {} }));
    setPageByWorkbook(prev => ({ ...prev, [workbookId]: 0 }));
    setWorkbook(prev =>
      prev.map(workbook =>
        workbook.id === workbookId
          ? { ...workbook, viewState: WORKBOOK_VIEW_EXPANDED }
          : workbook
      )
    );
  };

  const cycleSort = (workbookId, column) => {
    setSortByWorkbook(prev => {
      const cur = prev[workbookId];
      const addrField = cur?.addrField || "street";
      if (cur && cur.key === column) {
        if (cur.dir === "asc") {
          return {
            ...prev,
            [workbookId]: { key: column, dir: "desc", addrField }
          };
        }
        return {
          ...prev,
          [workbookId]: {
            key: null,
            dir: "asc",
            addrField: cur?.addrField || addrField
          }
        };
      }
      return {
        ...prev,
        [workbookId]: {
          key: column,
          dir: "asc",
          addrField: column === SORT_COL_ADDRESS ? addrField : undefined
        }
      };
    });
    setPageByWorkbook(prev => ({ ...prev, [workbookId]: 0 }));
  };

  const onAddressSortChipClick = (workbookId, field) => {
    setSortByWorkbook(prev => {
      const cur = prev[workbookId];
      if (cur?.key === SORT_COL_ADDRESS && cur.addrField === field) {
        const nextDir = cur.dir === "asc" ? "desc" : "asc";
        return {
          ...prev,
          [workbookId]: {
            key: SORT_COL_ADDRESS,
            dir: nextDir,
            addrField: field
          }
        };
      }
      return {
        ...prev,
        [workbookId]: {
          key: SORT_COL_ADDRESS,
          dir: "asc",
          addrField: field
        }
      };
    });
    setPageByWorkbook(prev => ({ ...prev, [workbookId]: 0 }));
  };

  const handlePageSizeChange = event => {
    event.stopPropagation();
    const n = Number(event.target.value);
    if (!Number.isFinite(n) || !PAGE_SIZE_OPTIONS.includes(n)) return;
    setPageSize(n);
    setPageByWorkbook({});
  };

  const setWorkbookViewState = (workbookId, viewState) => {
    setWorkbook(prev =>
      prev.map(workbook =>
        workbook.id === workbookId ? { ...workbook, viewState } : workbook
      )
    );
  };

  const cycleWorkbookViewFromMiniature = workbookId => {
    setWorkbook(prev =>
      prev.map(workbook => {
        if (workbook.id !== workbookId) return workbook;
        const current = getWorkbookViewState(workbook);
        if (workbook.isInbox) {
          return {
            ...workbook,
            viewState:
              current === WORKBOOK_VIEW_EXPANDED
                ? WORKBOOK_VIEW_COLLAPSED
                : WORKBOOK_VIEW_EXPANDED
          };
        }
        return {
          ...workbook,
          viewState:
            current === WORKBOOK_VIEW_MINI
              ? WORKBOOK_VIEW_EXPANDED
              : WORKBOOK_VIEW_MINI
        };
      })
    );
  };

  const moveTask = (fromWorkbookId, taskId, toWorkbookId, nextStatus) => {
    if (fromWorkbookId === toWorkbookId && !nextStatus) return;
    let movedTask = null;
    setWorkbook(prev =>
      prev
        .map(workbook => {
          if (workbook.id === fromWorkbookId) {
            const remaining = workbook.tasks.filter(task => {
              if (task.id !== taskId) return true;
              movedTask = task;
              return false;
            });
            return { ...workbook, tasks: remaining };
          }
          return workbook;
        })
        .map(workbook => {
          if (!movedTask || workbook.id !== toWorkbookId) return workbook;
          return {
            ...workbook,
            tasks: [
              ...workbook.tasks,
              { ...movedTask, status: nextStatus || movedTask.status }
            ]
          };
        })
    );
  };

  const addWorkbook = () => {
    setWorkbook(prev => [
      ...prev,
      {
        id: `custom:${Date.now()}`,
        number: String(prev.filter(entry => !entry.isInbox).length + 1),
        name: "Neue Arbeitsmappe",
        isInbox: false,
        viewState: WORKBOOK_VIEW_EXPANDED,
        fahrzeug: "",
        mitarbeiter: "",
        clearing: "",
        tasks: []
      }
    ]);
  };

  const toggleSelection = (workbookId, taskId) => {
    setSelectedByWorkbook(prev => ({
      ...prev,
      [workbookId]: {
        ...(prev[workbookId] || {}),
        [taskId]: !(prev[workbookId] || {})[taskId]
      }
    }));
  };

  const clearSelection = workbookId => {
    setSelectedByWorkbook(prev => ({ ...prev, [workbookId]: {} }));
  };

  const applyToSelected = (workbookId, fn) => {
    const selection = selectedByWorkbook[workbookId] || {};
    const selectedIds = Object.keys(selection).filter(id => selection[id]);
    if (selectedIds.length === 0) return;

    setWorkbook(prev =>
      prev.map(workbook =>
        workbook.id !== workbookId
          ? workbook
          : {
              ...workbook,
              tasks: workbook.tasks.map(task =>
                selectedIds.includes(task.id) ? fn(task) : task
              )
            }
      )
    );
  };

  const batchMoveToWorkbook = (fromWorkbookId, toWorkbookId) => {
    const selection = selectedByWorkbook[fromWorkbookId] || {};
    const selectedIds = Object.keys(selection).filter(id => selection[id]);
    if (selectedIds.length === 0) return;

    let moved = [];
    setWorkbook(prev =>
      prev
        .map(workbook => {
          if (workbook.id !== fromWorkbookId) return workbook;
          moved = workbook.tasks.filter(task => selectedIds.includes(task.id));
          return {
            ...workbook,
            tasks: workbook.tasks.filter(task => !selectedIds.includes(task.id))
          };
        })
        .map(workbook =>
          workbook.id !== toWorkbookId
            ? workbook
            : { ...workbook, tasks: [...workbook.tasks, ...moved] }
        )
    );

    clearSelection(fromWorkbookId);
  };

  const umsatzConfig = getWencoUmsatzConfig();

  const workbookTableColSpan = wencoDemoMode ? 6 : WORKBOOK_TABLE_COLSPAN;

  const renderWorkbookCard = workbook => {
    const tab = getActiveTab(workbook.id, workbook);
          const driverId = getWorkbookDriverId(workbook);
          const driverFirstname = getDriverFirstname(driverId, drivers);
          const showDriverFirstname =
            !wencoDemoMode &&
            driverId &&
            driverFirstname &&
            driverFirstname !== driverId;
          const tasks = workbook.tasks;
          const plannedRevenueTotalTeur = sumWencoUmsatzTeur(
            tasks.map(task => task.plannedRevenueTeur)
          );
          const hasPlannedRevenue =
            umsatzConfig.showInWorkbookHeader && plannedRevenueTotalTeur > 0;
          const counts = {
            vordisponiert: tasks.filter(
              task => task.status === "vordisponiert" && !isTaskDone(task)
            ).length,
            ausstehend: tasks.filter(
              task => task.status === "ausstehend" && !isTaskDone(task)
            ).length,
            disponiert: tasks.filter(
              task => task.status === "disponiert" && !isTaskDone(task)
            ).length,
            erledigt: tasks.filter(isTaskDone).length,
            wiederkehrend: tasks.filter(
              task => task.status === "wiederkehrend" && !isTaskDone(task)
            ).length
          };

          const isMini = isWorkbookMini(workbook);
          const isExpanded = isWorkbookExpanded(workbook);
          const useMiniatureLayout = !workbook.isInbox;
          const showTabs = workbook.isInbox || isExpanded;

          const visibleRows = getVisibleRowsForTab(workbook, tab);
          const sortCfgWb = sortByWorkbook[workbook.id] || null;
          const sortedRows = sortVisibleRows(visibleRows, sortCfgWb);
          const totalSorted = sortedRows.length;
          const totalPages = Math.max(1, Math.ceil(totalSorted / pageSize));
          const rawPage = pageByWorkbook[workbook.id] ?? 0;
          const currentPage = Math.min(Math.max(0, rawPage), totalPages - 1);
          const pageStart = currentPage * pageSize;
          const pageRows = sortedRows.slice(pageStart, pageStart + pageSize);
          const tbodyPadCount =
            totalPages > 1 ? Math.max(0, pageSize - pageRows.length) : 0;
          const dateSortColumn =
            tab === "wiederkehrend" ? SORT_COL_RHYTHMUS : SORT_COL_TERMIN;

          const selection = selectedByWorkbook[workbook.id] || {};
          const selectedIds = Object.keys(selection).filter(
            id => selection[id]
          );
          const selectedCount = selectedIds.length;
          const allOnPageSelected =
            pageRows.length > 0 && pageRows.every(row => selection[row.id]);

          return (
            <WorkbookCard
              key={workbook.id}
              data-workbook-id={workbook.id}
              $compact={isMini}
            >
              <WorkbookHeader $compact={isMini}>
                {useMiniatureLayout ? (
                  <HeaderTop $compact={isMini}>
                    <MiniatureBar
                      type="button"
                      $compact={isMini}
                      onClick={() =>
                        cycleWorkbookViewFromMiniature(workbook.id)
                      }
                    >
                      <Chevron $compact={isMini}>
                        {isExpanded ? "▾" : "▸"}
                      </Chevron>
                      <MiniatureId $compact={isMini}>
                        {getWorkbookMiniLabel(workbook)}
                        {showDriverFirstname ? (
                          <MiniatureDriverName $compact={isMini}>
                            {driverFirstname}
                          </MiniatureDriverName>
                        ) : null}
                      </MiniatureId>
                      {isMini ? (
                        <MiniatureMeta $compact={isMini}>
                          <MiniatureTab $compact={isMini}>
                            {getTabDisplayLabel(tab)}
                          </MiniatureTab>
                          <MiniatureCountBadge $compact={isMini}>
                            {counts[tab] ?? 0}
                          </MiniatureCountBadge>
                        </MiniatureMeta>
                      ) : null}
                    </MiniatureBar>
                    {hasPlannedRevenue ? (
                      <PlannedRevenueBadge
                        title={umsatzConfig.workbookHint}
                      >
                        {umsatzConfig.workbookLabel}:{" "}
                        <strong>
                          {formatWencoWorkbookRevenue(plannedRevenueTotalTeur)}
                        </strong>
                      </PlannedRevenueBadge>
                    ) : null}
                    <MenuWrap ref={menuRef}>
                      <MenuTrigger
                        $compact={isMini}
                        onClick={event => {
                          event.stopPropagation();
                          setMenuOpenFor(current =>
                            current === workbook.id ? null : workbook.id
                          );
                        }}
                      >
                        ⋮
                      </MenuTrigger>
                      {menuOpenFor === workbook.id && (
                        <Menu>
                          {useMiniatureLayout && !isMini ? (
                            <MenuItem
                              onClick={() => {
                                setWorkbookViewState(
                                  workbook.id,
                                  WORKBOOK_VIEW_MINI
                                );
                                setMenuOpenFor(null);
                              }}
                            >
                              Miniaturansicht
                            </MenuItem>
                          ) : null}
                          <MenuItem
                            onClick={() => {
                              const newName = window.prompt(
                                "Arbeitsmappe umbenennen",
                                workbook.name
                              );
                              if (newName) {
                                setWorkbook(prev =>
                                  prev.map(entry =>
                                    entry.id === workbook.id
                                      ? { ...entry, name: newName }
                                      : entry
                                  )
                                );
                              }
                              setMenuOpenFor(null);
                            }}
                          >
                            Arbeitsmappe umbenennen
                          </MenuItem>
                          <MenuItem
                            danger
                            onClick={() => {
                              setWorkbook(prev =>
                                prev.filter(entry => entry.id !== workbook.id)
                              );
                              setMenuOpenFor(null);
                            }}
                          >
                            Arbeitsmappe löschen
                          </MenuItem>
                        </Menu>
                      )}
                    </MenuWrap>
                  </HeaderTop>
                ) : (
                  <HeaderMain
                    onClick={() =>
                      cycleWorkbookViewFromMiniature(workbook.id)
                    }
                  >
                    <Chevron>{isExpanded ? "▾" : "▸"}</Chevron>
                    <WorkbookTitle>{workbook.name}</WorkbookTitle>
                    {hasPlannedRevenue ? (
                      <PlannedRevenueBadge
                        title={umsatzConfig.workbookHint}
                      >
                        {umsatzConfig.workbookLabel}:{" "}
                        <strong>
                          {formatWencoWorkbookRevenue(plannedRevenueTotalTeur)}
                        </strong>
                      </PlannedRevenueBadge>
                    ) : null}
                  </HeaderMain>
                )}

                {showTabs ? (
                  <TabsClip $capBottom={!isExpanded}>
                    <TabsRow onClick={event => event.stopPropagation()}>
                      {(wencoDemoMode ? TAB_ORDER_WENCO_DEMO : TAB_ORDER).map(
                        currentTab => {
                          const isActive = tab === currentTab;
                          const count = counts[currentTab];
                          return (
                            <React.Fragment key={currentTab}>
                              {!wencoDemoMode &&
                                currentTab === "wiederkehrend" && (
                                  <TabsSpacer />
                                )}
                              <TabButton
                                type="button"
                                active={isActive}
                                onClick={() =>
                                  setActiveTab(workbook.id, currentTab)
                                }
                              >
                                {getTabDisplayLabel(currentTab)}
                                <CountBadge $tabActive={isActive}>
                                  {count}
                                </CountBadge>
                              </TabButton>
                            </React.Fragment>
                          );
                        }
                      )}
                    </TabsRow>
                  </TabsClip>
                ) : null}
              </WorkbookHeader>

              {isExpanded && (
                <WorkbookBody>
                  {selectedCount > 0 && (
                    <BatchBar>
                      <SelectedLabel>
                        {selectedCount} ausgewählt
                        <button
                          type="button"
                          onClick={() => clearSelection(workbook.id)}
                        >
                          ×
                        </button>
                      </SelectedLabel>

                      <SimpleSelect
                        onChange={event => {
                          if (!event.target.value) return;
                          applyToSelected(workbook.id, task => ({
                            ...task,
                            aufgabeStatus: event.target.value,
                            done:
                              event.target.value === "erledigt"
                                ? true
                                : task.done,
                            doneDate:
                              event.target.value === "erledigt"
                                ? new Date()
                                : task.doneDate
                          }));
                          event.target.value = "";
                        }}
                      >
                        <option value="">Status zuweisen</option>
                        <option value="offen">Offen</option>
                        <option value="in_arbeit">In Arbeit</option>
                        <option value="erledigt">Erledigt</option>
                      </SimpleSelect>

                      <SimpleSelect
                        onChange={event => {
                          const value = event.target.value;
                          if (!value) return;
                          if (value.startsWith("workbook:")) {
                            batchMoveToWorkbook(
                              workbook.id,
                              value.replace("workbook:", "")
                            );
                          }
                          if (value.startsWith("status:")) {
                            const status = value.replace("status:", "");
                            applyToSelected(workbook.id, task => ({
                              ...task,
                              status
                            }));
                            clearSelection(workbook.id);
                          }
                          event.target.value = "";
                        }}
                      >
                        <option value="">Verschieben nach</option>
                        {workbooks
                          .filter(entry => entry.id !== workbook.id)
                          .map(entry => (
                            <option
                              key={entry.id}
                              value={`workbook:${entry.id}`}
                            >
                              #{entry.number} {entry.name}
                            </option>
                          ))}
                        <option value="status:vordisponiert">
                          Status: vordisponiert
                        </option>
                        <option value="status:ausstehend">
                          Status: ausstehend
                        </option>
                        <option value="status:disponiert">
                          Status: disponiert
                        </option>
                      </SimpleSelect>

                      <DangerButton
                        type="button"
                        onClick={() => {
                          applyToSelected(workbook.id, () => ({
                            __delete__: true
                          }));
                          setWorkbook(prev =>
                            prev.map(entry =>
                              entry.id !== workbook.id
                                ? entry
                                : {
                                    ...entry,
                                    tasks: entry.tasks.filter(
                                      task => !task.__delete__
                                    )
                                  }
                            )
                          );
                          clearSelection(workbook.id);
                        }}
                      >
                        Löschen
                      </DangerButton>
                    </BatchBar>
                  )}

                  {visibleRows.length > 0 ? (
                    <React.Fragment>
                      <TableWrap>
                        <WorkbookTable>
                          <thead>
                            <tr>
                              <th>
                                <HeaderSelect>
                                  <HeaderCheckbox
                                    checked={allOnPageSelected}
                                    onChange={() => {
                                    setSelectedByWorkbook(prev => {
                                      const prevMap = {
                                        ...(prev[workbook.id] || {})
                                      };
                                      const next = { ...prevMap };
                                      if (allOnPageSelected) {
                                        pageRows.forEach(row => {
                                          next[row.id] = false;
                                        });
                                      } else {
                                        pageRows.forEach(row => {
                                          next[row.id] = true;
                                        });
                                      }
                                      return {
                                        ...prev,
                                        [workbook.id]: next
                                      };
                                    });
                                  }}
                                  />
                                </HeaderSelect>
                              </th>
                              <th>
                                <SortHeaderButton
                                  type="button"
                                  $active={sortCfgWb?.key === SORT_COL_NAME}
                                  onClick={() =>
                                    cycleSort(workbook.id, SORT_COL_NAME)
                                  }
                                >
                                  {wencoDemoMode ? "Kunde" : "Name"}
                                  {sortArrowFor(sortCfgWb, SORT_COL_NAME)}
                                </SortHeaderButton>
                              </th>
                              <th>
                                <AddressHeaderLine>
                                  <>
                                    {ADDRESS_LINE_SEGMENTS.map(
                                      ({ field, text, prefix }) => {
                                        const activeField =
                                          sortCfgWb?.key === SORT_COL_ADDRESS &&
                                          (sortCfgWb.addrField || "street") ===
                                            field;
                                        return (
                                          <React.Fragment key={field}>
                                            {prefix ? (
                                              <AddressSegSep>
                                                {prefix}
                                              </AddressSegSep>
                                            ) : null}
                                            <AddressSegBtn
                                              type="button"
                                              $active={activeField}
                                              aria-pressed={activeField}
                                              onClick={() =>
                                                onAddressSortChipClick(
                                                  workbook.id,
                                                  field
                                                )
                                              }
                                            >
                                              {text}
                                              {activeField ? (
                                                <SortArrowPair
                                                  activeDir={sortCfgWb.dir}
                                                />
                                              ) : null}
                                            </AddressSegBtn>
                                          </React.Fragment>
                                        );
                                      }
                                    )}
                                  </>
                                </AddressHeaderLine>
                              </th>
                              <th>
                                <SortHeaderButton
                                  type="button"
                                  $active={sortCfgWb?.key === SORT_COL_AUFGABE}
                                  onClick={() =>
                                    cycleSort(workbook.id, SORT_COL_AUFGABE)
                                  }
                                >
                                  Aufgabe
                                  {sortArrowFor(sortCfgWb, SORT_COL_AUFGABE)}
                                </SortHeaderButton>
                              </th>
                              {!wencoDemoMode ? (
                                <th>
                                  <SortHeaderButton
                                    type="button"
                                    $active={sortCfgWb?.key === SORT_COL_NETZ}
                                    onClick={() =>
                                      cycleSort(workbook.id, SORT_COL_NETZ)
                                    }
                                  >
                                    Netzwerk
                                    {sortArrowFor(sortCfgWb, SORT_COL_NETZ)}
                                  </SortHeaderButton>
                                </th>
                              ) : null}
                              {!wencoDemoMode ? (
                                <th>
                                  <SortHeaderButton
                                    type="button"
                                    $active={
                                      sortCfgWb?.key === SORT_COL_AUFTRAG
                                    }
                                    onClick={() =>
                                      cycleSort(workbook.id, SORT_COL_AUFTRAG)
                                    }
                                  >
                                    Auftrag
                                    {sortArrowFor(sortCfgWb, SORT_COL_AUFTRAG)}
                                  </SortHeaderButton>
                                </th>
                              ) : null}
                              <th>
                                <SortHeaderButton
                                  type="button"
                                  $active={sortCfgWb?.key === dateSortColumn}
                                  onClick={() =>
                                    cycleSort(workbook.id, dateSortColumn)
                                  }
                                >
                                  {tab === "wiederkehrend"
                                    ? "Rhythmus"
                                    : "Terminierung"}
                                  {sortArrowFor(sortCfgWb, dateSortColumn)}
                                </SortHeaderButton>
                              </th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {pageRows.map((task, index) => {
                              const isHindernis = isHindernisTask(task);
                              const isCompleted =
                                Boolean(task.done) ||
                                task.aufgabeStatus === "erledigt";
                              const statusDot =
                                task.aufgabeStatus === "in_arbeit";
                              const isSelected = Boolean(selection[task.id]);
                              const showCheckbox =
                                !isCompleted &&
                                !statusDot &&
                                (isSelected || hoveredTaskId === task.id);
                              const rowIndex = pageStart + index + 1;
                              return (
                                <tr
                                  key={task.id}
                                  data-selected={
                                    isSelected ? "true" : undefined
                                  }
                                  data-done={isCompleted ? "true" : undefined}
                                  data-hindernis={
                                    isHindernis ? "true" : undefined
                                  }
                                  data-inprogress={
                                    statusDot ? "true" : undefined
                                  }
                                  onMouseEnter={() => setHoveredTaskId(task.id)}
                                  onMouseLeave={() => setHoveredTaskId(null)}
                                >
                                  <td
                                    style={{
                                      boxShadow: isHindernis
                                        ? `inset 3px 0 0 ${WORKBOOK_HINDERNIS_ACCENT}`
                                        : isCompleted
                                        ? `inset 3px 0 0 ${WORKBOOK_DONE_ACCENT}`
                                        : statusDot
                                        ? `inset 3px 0 0 ${WORKBOOK_HINDERNIS_ACCENT}`
                                        : "none"
                                    }}
                                  >
                                    <RowSelect>
                                      {isHindernis ? (
                                        <HindernisMark
                                          width="13"
                                          height="13"
                                          viewBox="0 0 14 14"
                                          fill="none"
                                          aria-label="Hindernis"
                                        >
                                          <path
                                            d="M7 3.5v4.25M7 10h.01"
                                            stroke={WORKBOOK_HINDERNIS_ACCENT}
                                            strokeWidth="1.75"
                                            strokeLinecap="round"
                                          />
                                          <circle
                                            cx="7"
                                            cy="7"
                                            r="5.25"
                                            stroke={WORKBOOK_HINDERNIS_ACCENT}
                                            strokeWidth="1.25"
                                          />
                                        </HindernisMark>
                                      ) : isCompleted ? (
                                        <DoneCheck
                                          width="13"
                                          height="13"
                                          viewBox="0 0 14 14"
                                          fill="none"
                                        >
                                          <path
                                            d="M2 7l4 4 6-6"
                                            stroke={WORKBOOK_DONE_ACCENT}
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </DoneCheck>
                                      ) : statusDot ? (
                                        <InProgressDot />
                                      ) : showCheckbox ? (
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() =>
                                            toggleSelection(
                                              workbook.id,
                                              task.id
                                            )
                                          }
                                        />
                                      ) : (
                                        <span>{rowIndex}</span>
                                      )}
                                    </RowSelect>
                                  </td>
                                  <td>
                                    {wencoDemoMode ? (
                                      <CustomerNr $done={isCompleted}>
                                        {task.kundennummer || "—"}
                                      </CustomerNr>
                                    ) : (
                                      task.name || "—"
                                    )}
                                  </td>
                                  <td>{task.ort}</td>
                                  <td>
                                    <TaskTypeCell>
                                      {!task.isWencoTask ? (
                                        <>
                                          <span>{task.anzahl}</span>
                                          <PackageIcon muted={isCompleted} />
                                        </>
                                      ) : null}
                                      <span>
                                        {getAufgabeVerb(
                                          task,
                                          isCompleted && !isHindernis
                                        )}
                                      </span>
                                    </TaskTypeCell>
                                  </td>
                                  {!wencoDemoMode ? (
                                    <td>
                                      {task.netzwerk ? (
                                        <NetworkBadge $done={isCompleted}>
                                          {task.netzwerk}
                                        </NetworkBadge>
                                      ) : (
                                        "—"
                                      )}
                                    </td>
                                  ) : null}
                                  {!wencoDemoMode ? (
                                    <td title={task.auftragsnr}>
                                      {task.trackingCode ? (
                                        <OrderRefLink
                                          to={`/track/${encodeURIComponent(
                                            task.trackingCode
                                          )}`}
                                          $done={isCompleted}
                                        >
                                          {formatOrderNumber(task.auftragsnr)}
                                        </OrderRefLink>
                                      ) : (
                                        <OrderRef $done={isCompleted}>
                                          {formatOrderNumber(task.auftragsnr)}
                                        </OrderRef>
                                      )}
                                    </td>
                                  ) : null}
                                  <td>
                                    {tab === "wiederkehrend" ? (
                                      wencoDemoMode ? (
                                        <WencoRhythmusButton
                                          type="button"
                                          $done={isCompleted}
                                          onClick={() =>
                                            openRhythmusEditor(workbook.id, task)
                                          }
                                        >
                                          {task.rhythmus || "—"}
                                        </WencoRhythmusButton>
                                      ) : (
                                        task.rhythmus || "Wöchentlich"
                                      )
                                    ) : (
                                      formatTerminierungCell(
                                        task.faelligkeit,
                                        task.zeitfenster
                                      )
                                    )}
                                    {!wencoDemoMode && task.templateId ? (
                                      <SerieBadge>↻ Serie</SerieBadge>
                                    ) : null}
                                  </td>
                                  <td>
                                    <RowMoveMenu
                                      options={workbooks.filter(
                                        entry => entry.id !== workbook.id
                                      )}
                                      onMove={toWorkbookId =>
                                        moveTask(
                                          workbook.id,
                                          task.id,
                                          toWorkbookId,
                                          task.status
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            {Array.from({ length: tbodyPadCount }).map(
                              (_, padIdx) => (
                                <tr
                                  key={`wb-tbody-pad-${workbook.id}-${currentPage}-${padIdx}`}
                                  aria-hidden
                                  data-pad="true"
                                >
                                  <td colSpan={workbookTableColSpan} />
                                </tr>
                              )
                            )}
                          </tbody>
                        </WorkbookTable>
                      </TableWrap>
                      <PaginationBar>
                        <PaginationLeft>
                          <PaginationRange>
                            {totalSorted === 0
                              ? "—"
                              : `${pageStart + 1}–${Math.min(
                                  pageStart + pageSize,
                                  totalSorted
                                )} von ${totalSorted}`}
                          </PaginationRange>
                          <PageSizeLabel>
                            <PageSizeCaption>Einträge pro Seite</PageSizeCaption>
                            <PageSizeSelect
                              value={String(pageSize)}
                              onChange={handlePageSizeChange}
                              onClick={event => event.stopPropagation()}
                              onMouseDown={event => event.stopPropagation()}
                            >
                              {PAGE_SIZE_OPTIONS.map(n => (
                                <option key={`page-size-${n}`} value={String(n)}>
                                  {n}
                                </option>
                              ))}
                            </PageSizeSelect>
                          </PageSizeLabel>
                        </PaginationLeft>
                        <PaginationNav aria-label="Seiten">
                          <PageIconButton
                            type="button"
                            title="Erste Seite"
                            disabled={currentPage <= 0}
                            onClick={() =>
                              setPageByWorkbook(p => ({
                                ...p,
                                [workbook.id]: 0
                              }))
                            }
                          >
                            {"|<"}
                          </PageIconButton>
                          <PageIconButton
                            type="button"
                            title="Vorherige Seite"
                            disabled={currentPage <= 0}
                            onClick={() =>
                              setPageByWorkbook(p => ({
                                ...p,
                                [workbook.id]: Math.max(
                                  0,
                                  (p[workbook.id] ?? 0) - 1
                                )
                              }))
                            }
                          >
                            {"<"}
                          </PageIconButton>
                          {buildPaginationPageList(currentPage, totalPages).map(
                            (entry, idx) =>
                              entry === "ellipsis" ? (
                                <PaginationEllipsis key={`e-${idx}`}>
                                  …
                                </PaginationEllipsis>
                              ) : (
                                <PageNumButton
                                  key={entry}
                                  type="button"
                                  title={`Seite ${entry}`}
                                  $active={entry === currentPage + 1}
                                  aria-current={
                                    entry === currentPage + 1
                                      ? "page"
                                      : undefined
                                  }
                                  onClick={() =>
                                    setPageByWorkbook(p => ({
                                      ...p,
                                      [workbook.id]: entry - 1
                                    }))
                                  }
                                >
                                  {entry}
                                </PageNumButton>
                              )
                          )}
                          <PageIconButton
                            type="button"
                            title="Nächste Seite"
                            disabled={currentPage >= totalPages - 1}
                            onClick={() =>
                              setPageByWorkbook(p => ({
                                ...p,
                                [workbook.id]: Math.min(
                                  totalPages - 1,
                                  (p[workbook.id] ?? 0) + 1
                                )
                              }))
                            }
                          >
                            {">"}
                          </PageIconButton>
                          <PageIconButton
                            type="button"
                            title="Letzte Seite"
                            disabled={currentPage >= totalPages - 1}
                            onClick={() =>
                              setPageByWorkbook(p => ({
                                ...p,
                                [workbook.id]: totalPages - 1
                              }))
                            }
                          >
                            {">|"}
                          </PageIconButton>
                        </PaginationNav>
                      </PaginationBar>
                    </React.Fragment>
                  ) : (
                    <EmptyTabMessage>Keine Aufgaben zugewiesen</EmptyTabMessage>
                  )}
                </WorkbookBody>
              )}
            </WorkbookCard>
    );
  };

  const renderWorkbookSections = () => {
    const sections = [];
    let miniBatch = [];

    const flushMiniBatch = () => {
      if (miniBatch.length === 0) return;
      sections.push(
        <WorkbookMiniGrid key={`mini-${miniBatch.map(w => w.id).join("-")}`}>
          {miniBatch.map(renderWorkbookCard)}
        </WorkbookMiniGrid>
      );
      miniBatch = [];
    };

    filtered.forEach(workbook => {
      if (!workbook.isInbox && isWorkbookMini(workbook)) {
        miniBatch.push(workbook);
        return;
      }
      flushMiniBatch();
      sections.push(
        <React.Fragment key={workbook.id}>
          {renderWorkbookCard(workbook)}
        </React.Fragment>
      );
    });
    flushMiniBatch();
    return sections;
  };

  return (
    <WorkbookRoot>
      <WorkbookList>{renderWorkbookSections()}</WorkbookList>
      <WencoRhythmusConfigOverlay
        open={Boolean(wencoDemoMode && rhythmusEditor)}
        initialConfig={rhythmusEditor?.initialConfig}
        onCancel={closeRhythmusEditor}
        onApply={applyRhythmusEditor}
      />
    </WorkbookRoot>
  );
};

WorkbookView.propTypes = {
  shipments: PropTypes.shape({}),
  drivers: PropTypes.shape({}),
  searchText: PropTypes.string.isRequired,
  onSearchTextChange: PropTypes.func.isRequired
};

WorkbookView.defaultProps = {
  shipments: {},
  drivers: {}
};

RowMoveMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      number: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  onMove: PropTypes.func.isRequired
};

PackageIcon.propTypes = {
  muted: PropTypes.bool
};

PackageIcon.defaultProps = {
  muted: false
};

const WorkbookRoot = styled.div`
  background: #f7f6f2;
  min-height: 100%;
`;

const WorkbookSchedulingTopbarRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
`;

const TopbarDateWrap = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
`;

const TopbarCount = styled.span`
  font-size: 12px;
  color: #5f5e5a;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TopbarViewToggle = styled.button`
  border: 1px solid #d6d7da;
  background: #fff;
  color: #333;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
`;

const TopbarRightCluster = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const TopbarTools = styled.div`
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Search = styled.input`
  background: #fff;
  border: 1px solid #d6d7da;
  color: #333;
  height: 30px;
  border-radius: 6px;
  width: 220px;
  max-width: 100%;
  padding: 0 10px;
  font-size: 12px;
  outline: none;
  &::placeholder {
    color: #888780;
  }
`;

const CreateButton = styled.button`
  background: #5dcaa5;
  border: 0;
  color: #05362f;
  border-radius: 6px;
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const WorkbookList = styled.div`
  padding: 10px 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WorkbookMiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  width: 100%;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const WorkbookCard = styled.div`
  border: 1px solid #e2e0da;
  border-radius: ${({ $compact }) => ($compact ? "8px" : "10px")};
  background: #fff;
  overflow: visible;
  width: 100%;
`;

const WorkbookHeader = styled.div`
  padding: ${({ $compact }) => ($compact ? "5px 6px 0" : "10px 10px 0")};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "4px" : "8px")};
  padding-bottom: ${({ $compact }) => ($compact ? "5px" : "10px")};
`;

const MiniatureBar = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "4px" : "8px")};
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
`;

const MiniatureId = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "4px" : "6px")};
  color: #1a1a1a;
  font-size: ${({ $compact }) => ($compact ? "12px" : "14px")};
  line-height: 1.2;
  font-weight: 700;
  flex-shrink: 0;
`;

const MiniatureDriverName = styled.span`
  display: inline-block;
  flex-shrink: 0;
  padding: ${({ $compact }) => ($compact ? "1px 5px" : "2px 8px")};
  border-radius: ${({ $compact }) => ($compact ? "3px" : "4px")};
  border: 1px solid #d4e4f0;
  background: #eef5fa;
  color: #3d5a73;
  font-size: ${({ $compact }) => ($compact ? "10px" : "11px")};
  line-height: 1.25;
  font-weight: 600;
  white-space: nowrap;
`;

const MiniatureMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "4px" : "6px")};
  flex-shrink: 0;
`;

const MiniatureTab = styled.span`
  color: #888780;
  font-size: ${({ $compact }) => ($compact ? "10px" : "12px")};
  line-height: 1.2;
  white-space: nowrap;
`;

const MiniatureCountBadge = styled.span`
  display: inline-block;
  min-width: 1.2em;
  font-size: ${({ $compact }) => ($compact ? "10px" : "11px")};
  line-height: 1.25;
  text-align: center;
  border: 1px solid #e2e0da;
  background: #f4f3ef;
  border-radius: 3px;
  padding: ${({ $compact }) => ($compact ? "0 4px" : "1px 5px")};
  color: #888780;
  flex-shrink: 0;
`;

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  cursor: pointer;
`;

const Chevron = styled.span`
  color: #888780;
  font-size: ${({ $compact }) => ($compact ? "10px" : "12px")};
  flex-shrink: 0;
  line-height: 1;
`;

const WorkbookTitle = styled.strong`
  color: #1a1a1a;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 600;
`;

const PlannedRevenueBadge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #d9e7d4;
  background: #f4faf1;
  color: #3f5f32;
  font-size: 11px;
  line-height: 1.4;
  strong {
    font-weight: 700;
  }
`;

const TabsRow = styled.div`
  display: flex;
  align-items: flex-end;
`;

const TabsClip = styled.div`
  ${({ $capBottom }) =>
    $capBottom
      ? `
    overflow: hidden;
    border-radius: 0 0 9px 9px;
    padding-bottom: 8px;
  `
      : ""}
`;

const TabsSpacer = styled.span`
  flex: 1;
`;

const TabButton = styled.button`
  border: ${({ active }) =>
    active ? "1px solid #e2e0da" : "1px solid transparent"};
  border-bottom: ${({ active }) =>
    active ? "1px solid #fff" : "1px solid transparent"};
  border-radius: 8px 8px 0 0;
  background: ${({ active }) => (active ? "#fff" : "transparent")};
  font-size: 12px;
  color: ${({ active }) => (active ? "#1a1a1a" : "#b4b2a9")};
  padding: 7px 10px;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: ${({ active }) => (active ? "-1px" : 0)};
`;

const CountBadge = styled.span`
  margin-left: 6px;
  display: inline-block;
  min-width: 1.35em;
  font-size: 11px;
  line-height: 1.35;
  text-align: center;
  border: 1px solid #e2e0da;
  background: #f7f6f2;
  border-radius: 4px;
  padding: 1px 5px;
  color: ${({ $tabActive }) => ($tabActive ? "#5f5e5a" : "#888780")};
`;

const WorkbookBody = styled.div`
  border: 0;
  border-top: 1px solid #e2e0da;
  border-radius: 0 0 9px 9px;
  overflow: hidden;
`;

const EmptyTabMessage = styled.p`
  margin: 0;
  padding: 20px 10px;
  font-size: 12px;
  color: #888780;
  background: #fff;
`;

const BatchBar = styled.div`
  background: #e8f7f1;
  border-bottom: 1px solid #c8e6d8;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 10px;
`;

const SelectedLabel = styled.span`
  color: #0f7a55;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  button {
    border: 0;
    background: transparent;
    cursor: pointer;
    color: #0f7a55;
    font-size: 16px;
    line-height: 1;
  }
`;

const SimpleSelect = styled.select`
  border: 1px solid #c8e6d8;
  border-radius: 6px;
  background: #fff;
  color: #1a1a1a;
  font-size: 12px;
  height: 30px;
  padding: 0 8px;
`;

const DangerButton = styled.button`
  border: 1px solid #f1c8c8;
  border-radius: 6px;
  background: #fff;
  color: #a32d2d;
  font-size: 12px;
  height: 30px;
  padding: 0 10px;
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const WorkbookTable = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  box-sizing: border-box;
  th,
  td {
    box-sizing: border-box;
    border-bottom: 1px dashed #eeece7;
    padding: 8px 10px;
    font-size: 12px;
    text-align: left;
    white-space: nowrap;
  }
  th {
    color: #888780;
    font-weight: 500;
  }
  thead th {
    border-top: 0;
    border-bottom: 1px solid #e2e0da;
    white-space: normal;
    vertical-align: top;
  }
  thead th:first-child {
    vertical-align: middle;
    width: 34px;
    text-align: center;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  tbody tr {
    background: #fff;
  }
  tbody tr[data-selected="true"] {
    background: #f0faf6;
  }
  tbody tr td {
    color: #1a1a1a;
    min-height: ${WORKBOOK_TBODY_ROW_HEIGHT_PX}px;
    vertical-align: middle;
  }
  tbody tr[data-pad] {
    height: ${WORKBOOK_TBODY_ROW_HEIGHT_PX}px;
    pointer-events: none;
  }
  tbody tr[data-pad] td {
    height: ${WORKBOOK_TBODY_ROW_HEIGHT_PX}px;
    min-height: ${WORKBOOK_TBODY_ROW_HEIGHT_PX}px;
    padding: 8px 10px;
    vertical-align: middle;
    background: #fff;
    color: transparent;
  }
  tbody tr td:first-child {
    position: relative;
  }
  tbody tr[data-done="true"] td {
    color: #b4b2a9;
  }
  tbody tr[data-done="true"] td span,
  tbody tr[data-done="true"] td strong,
  tbody tr[data-done="true"] td button {
    color: #b4b2a9;
  }
`;

const NetworkBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.35;
  white-space: nowrap;
  color: ${({ $done }) => ($done ? "#b4b2a9" : "#1a1a1a")};
  border: 1px solid ${({ $done }) => ($done ? "#d6d4ce" : "#1a1a1a")};
  background: transparent;
`;

const orderRefStyles = ({ $done }) =>
  $done
    ? `
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
  `
    : "";

const OrderRef = styled.span`
  ${orderRefStyles}
`;

const OrderRefLink = styled(Link)`
  color: inherit;
  cursor: pointer;
  ${orderRefStyles}
  &:hover {
    color: #1a1a1a;
  }
`;

const HeaderSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 14px;
`;

const HeaderCheckbox = styled.input.attrs({ type: "checkbox" })`
  width: 13px;
  height: 13px;
  margin: 0;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

const RowSelect = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  min-height: 14px;
  span {
    font-size: 11px;
    color: #b4b2a9;
    min-width: 13px;
    text-align: center;
    font-weight: 600;
  }
  input {
    width: 13px;
    height: 13px;
    cursor: pointer;
  }
`;

const DoneCheck = styled.svg`
  display: block;
`;

const HindernisMark = styled.svg`
  display: block;
`;

const InProgressDot = styled.span`
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e6a020;
`;

const SerieBadge = styled.span`
  margin-left: 6px;
  font-size: 10px;
  color: #888780;
  border: 1px solid #e2e0da;
  border-radius: 4px;
  padding: 1px 4px;
`;

const CustomerNr = styled.span`
  font-size: 12px;
  color: ${({ $done }) => ($done ? "#b4b2a9" : "#1a1a1a")};
  line-height: 1.3;
`;

const WencoRhythmusButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-offset: 2px;
  text-decoration-color: ${({ $done }) => ($done ? "#d6d4ce" : "#b4b2a9")};
  color: ${({ $done }) => ($done ? "#b4b2a9" : "inherit")};

  &:hover {
    color: #1a1a1a;
    text-decoration-color: #888780;
  }
`;

const SortHeaderButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: ${({ $active }) => ($active ? "#1a1a1a" : "inherit")};
  font-weight: ${({ $active }) => ($active ? 700 : "inherit")};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0;
  &:hover {
    color: #1a1a1a;
  }
`;

const AddressHeaderLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  line-height: 1.4;
  font-size: 12px;
  max-width: 100%;
`;

const AddressSegSep = styled.span`
  color: #888780;
  font-weight: 400;
  user-select: none;
  white-space: pre;
`;

const AddressSegBtn = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  color: #888780;
  font-weight: 400;
  text-decoration: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0;
  &:hover {
    color: #1a1a1a;
  }
  ${({ $active }) =>
    $active
      ? `
    color: #1a1a1a;
    font-weight: 700;
  `
      : ""}
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 4px 10px;
  background: #fafaf9;
  border-top: 1px dashed #eeece7;
  font-size: 12px;
  color: #5f5e5a;
`;

const PaginationLeft = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 20px;
  min-width: 0;
`;

const PaginationRange = styled.span`
  flex-shrink: 0;
  color: #5f5e5a;
  font-size: 12px;
  line-height: 30px;
  height: 30px;
  white-space: nowrap;
`;

const PaginationNav = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-left: auto;
`;

const PageIconButton = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #5f5e5a;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
    color: #1a1a1a;
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const PageNumButton = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border: 1px solid transparent;
  border-radius: 50%;
  background: transparent;
  color: #5f5e5a;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #1a1a1a;
  }
  ${({ $active }) =>
    $active
      ? `
    background: #e2e0da;
    color: #1a1a1a;
    font-weight: 600;
    border-color: #d6d7da;
  `
      : ""}
`;

const PaginationEllipsis = styled.span`
  padding: 0 4px;
  color: #888780;
  user-select: none;
  font-size: 12px;
  line-height: 30px;
`;

const PageSizeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  margin: 0;
  font-size: 12px;
  color: #5f5e5a;
  flex-shrink: 0;
`;

const PageSizeCaption = styled.span`
  font-size: 12px;
  line-height: 30px;
  height: 30px;
  white-space: nowrap;
`;

const PageSizeSelect = styled.select`
  flex-shrink: 0;
  margin: 0;
  font-size: 12px;
  border: 1px solid #e2e0da;
  border-radius: 4px;
  height: 30px;
  box-sizing: border-box;
  padding: 0 8px;
  line-height: 1;
  background: #fff;
  vertical-align: middle;
`;

const TaskTypeCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MoveMenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MoveButton = styled.button`
  width: 28px;
  height: 22px;
  border: 1px solid #e2e0da;
  border-radius: 6px;
  background: #fafaf9;
  color: #888780;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const MoveMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e2e0da;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  z-index: 20;
  overflow: hidden;
`;

const MoveMenuItem = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 8px 10px;
  color: #1a1a1a;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #f7f6f2;
  }
`;

const MenuWrap = styled.div`
  margin-left: auto;
  position: relative;
`;

const MenuTrigger = styled.button`
  border: 0;
  background: transparent;
  color: #888780;
  font-size: ${({ $compact }) => ($compact ? "14px" : "18px")};
  cursor: pointer;
  line-height: 1;
  padding: ${({ $compact }) => ($compact ? "0 1px" : "0")};
  flex-shrink: 0;
`;

const Menu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 220px;
  border: 1px solid #e2e0da;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);
  z-index: 20;
`;

const MenuItem = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  font-size: 13px;
  color: ${({ danger }) => (danger ? "#a32d2d" : "#1a1a1a")};
  padding: 9px 12px;
  cursor: pointer;
`;

export default WorkbookView;
